import type { InstallmentStatus } from "@/types/installment";

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const RANGES = ["All Time", "Today", "Tomorrow", "This Week", "This Month", "Last Month", "Custom Range"];
export const DEFAULT_RANGE = "All Time";

export interface StatusMeta {
  label: string;
  text: string;
  bg: string;
  dot: string;
}

export const DEFAULT_INSTALLMENT_STATUS_META: StatusMeta = {
  label: "Scheduled",
  text: "text-slate-600",
  bg: "bg-badgeSlate-bg",
  dot: "bg-badgeSlate",
};

export const INSTALLMENT_STATUS_META: Record<InstallmentStatus, StatusMeta> = {
  SCHEDULED: { label: "Scheduled", text: "text-slate-600", bg: "bg-badgeSlate-bg", dot: "bg-badgeSlate" },
  PRESENTED: { label: "Presented", text: "text-sky-700", bg: "bg-info-bg", dot: "bg-info" },
  COLLECTED: { label: "Collected", text: "text-emerald-700", bg: "bg-success-bg", dot: "bg-success" },
  COLLECTED_OFFLINE: { label: "Collected Offline", text: "text-[#1E40AF]", bg: "bg-[#EFF6FF]", dot: "bg-[#1E40AF]" },
  IN_PROCESS: { label: "In Process", text: "text-amber-700", bg: "bg-warning-bg", dot: "bg-warning" },
  PROCESSING: { label: "Processing", text: "text-amber-700", bg: "bg-warning-bg", dot: "bg-warning" },
  COLLECTION_INITIATED: { label: "Initiated", text: "text-sky-700", bg: "bg-info-bg", dot: "bg-info" },
  PENDING: { label: "Pending", text: "text-amber-700", bg: "bg-warning-bg", dot: "bg-warning" },
  INITIATED: { label: "Initiated", text: "text-sky-700", bg: "bg-info-bg", dot: "bg-info" },
  FAILED: { label: "Failed", text: "text-red-700", bg: "bg-danger-bg", dot: "bg-danger" },
  SETTLEMENT_PENDING: { label: "Settling", text: "text-amber-700", bg: "bg-warning-bg", dot: "bg-warning" },
  SETTLED: { label: "Settled", text: "text-[#047857]", bg: "bg-[#D1FAE5]", dot: "bg-[#047857]" },
  SETTLEMENT_FAILED: { label: "Settlement Failed", text: "text-red-700", bg: "bg-danger-bg", dot: "bg-danger" },
  CANCELLED: { label: "Cancelled", text: "text-slate-600", bg: "bg-badgeSlate-bg", dot: "bg-badgeSlate" },
  SKIPPED: { label: "Skipped", text: "text-slate-600", bg: "bg-badgeSlate-bg", dot: "bg-badgeSlate" },
};

// Statuses that count as a completed collection, including offline collections
// recorded directly by the retailer.
export const COLLECTED_OR_OFFLINE: InstallmentStatus[] = [
  "COLLECTED",
  "SETTLEMENT_PENDING",
  "SETTLED",
  "SETTLEMENT_FAILED",
  "COLLECTED_OFFLINE",
];

export function formatINR(n?: number | string | null): string {
  const num = Number(n);
  const safeNum = Number.isFinite(num) ? num : 0;
  return `\u20B9${safeNum.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function toDate(iso: string): Date {
  const d = new Date(`${iso}T00:00:00`);
  return d;
}

export function formatDueDate(iso: string): string {
  const d = toDate(iso);
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function formatDueShort(iso: string): string {
  const d = toDate(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export function dayName(iso: string): string {
  const d = toDate(iso);
  return d.toLocaleDateString("en-US", { weekday: "long" });
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  let h = d.getHours();
  const m = pad(d.getMinutes());
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${h}:${m} ${ampm}`;
}

function isoNow(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function toYmd(dStr?: string): string {
  if (!dStr) return "";
  const directMatch = dStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (directMatch) return `${directMatch[1]}-${directMatch[2]}-${directMatch[3]}`;
  const d = new Date(dStr);
  if (!isNaN(d.getTime())) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  const parts = dStr.trim().split(/[\s-]+/);
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const mIdx = MONTHS.findIndex((m) => m.toLowerCase() === parts[1].slice(0, 3).toLowerCase());
    const yr = parseInt(parts[2], 10);
    if (!isNaN(day) && mIdx !== -1 && !isNaN(yr)) {
      return `${yr}-${pad(mIdx + 1)}-${pad(day)}`;
    }
  }
  return dStr;
}

export function inDueRange(dueDate: string, range: string, from: string, to: string): boolean {
  const now = new Date();
  const today = isoNow(now);
  const dueYmd = toYmd(dueDate);
  switch (range) {
    case "Today":
      return dueYmd === today;
    case "Tomorrow": {
      const t = new Date(now);
      t.setDate(t.getDate() + 1);
      return dueYmd === isoNow(t);
    }
    case "This Week": {
      const end = new Date(now);
      end.setDate(end.getDate() + 6);
      return dueYmd >= today && dueYmd <= isoNow(end);
    }
    case "This Month":
      return dueYmd.slice(0, 7) === today.slice(0, 7);
    case "Last Month": {
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return dueYmd.slice(0, 7) === isoNow(lm).slice(0, 7);
    }
    case "Custom Range": {
      const fromYmd = toYmd(from);
      const toYmdStr = toYmd(to);
      if (fromYmd && dueYmd < fromYmd) return false;
      if (toYmdStr && dueYmd > toYmdStr) return false;
      return true;
    }
    default:
      return true;
  }
}

export interface FailureStat {
  reason: string;
  count: number;
  percentage: number;
}

export function aggregateFailures(items: { failureReason: string | null }[]): FailureStat[] {
  const map = new Map<string, number>();
  items.forEach((t) => {
    if (t.failureReason) map.set(t.failureReason, (map.get(t.failureReason) ?? 0) + 1);
  });
  const total = Array.from(map.values()).reduce((a, b) => a + b, 0);
  return Array.from(map.entries())
    .map(([reason, count]) => ({
      reason,
      count,
      percentage: total ? Math.round((count / total) * 100) : 0,
    }))
    .sort((a, b) => b.count - a.count);
}